/**
 * The rows of a session's transcript (TRA-1327): what a person sent, the
 * agent's text, its tool calls, its approval requests and the session's
 * notes. Built by transcript.ts, so these only render.
 */

import { Check, CircleAlert, Hand, LoaderCircle } from 'lucide-react'
import { useId, useState } from 'react'
import { Link } from 'react-router-dom'

import { Markdown } from '@/components/review/Markdown'
import { presetFieldLabel, presetValue } from '@/components/training/launch'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useHasPassed } from '@/hooks/useHasPassed'
import { cn } from '@/lib/utils'

import { toolInput, toolSummary } from './display'
import { toolLabel } from './transcript'
import type {
  AgentText,
  ApprovalRequest,
  LaunchRequest,
  LaunchState,
  Note,
  ToolCall,
  ToolGroup,
  UserTurn,
} from './transcript'

const LABEL = 'font-mono text-[10px] uppercase tracking-wider text-muted-foreground'

function Omitted({ count }: { count: number }): React.JSX.Element | null {
  if (count === 0) return null
  return (
    <p className={LABEL}>The runner cut {count.toLocaleString('en-US')} characters from this text</p>
  )
}

export function UserRow({ turn, you }: { turn: UserTurn; you: string | null }): React.JSX.Element {
  return (
    <div className="ml-auto max-w-[85%] space-y-1">
      <p className={cn(LABEL, 'text-right')}>{turn.by === you ? 'You' : turn.by}</p>
      <div className="whitespace-pre-wrap break-words border border-border p-3 text-sm">
        {turn.text}
      </div>
    </div>
  )
}

export function AgentRow({ said }: { said: AgentText }): React.JSX.Element {
  return (
    <div className="space-y-1">
      <p className={LABEL}>Agent</p>
      <Markdown text={said.text} className="text-sm" />
      <Omitted count={said.omitted} />
    </div>
  )
}

function CallState({ call }: { call: ToolCall }): React.JSX.Element {
  if (call.awaitingApproval) return <Hand className="h-3.5 w-3.5 text-warning" aria-label="Waiting for approval" />
  if (call.result === null) {
    return <LoaderCircle className="h-3.5 w-3.5 animate-spin" aria-label="Running" />
  }
  if (call.result.isError) {
    return <CircleAlert className="h-3.5 w-3.5 text-destructive" aria-label="Error" />
  }
  return <Check className="h-3.5 w-3.5" aria-label="Done" />
}

function CallRow({ call }: { call: ToolCall }): React.JSX.Element {
  return (
    <li>
      <details className="group">
        <summary className="grid cursor-pointer grid-cols-[1rem_minmax(0,1fr)] items-start gap-2 px-3 py-2 text-sm hover:bg-muted">
          <span className="pt-0.5">
            <CallState call={call} />
          </span>
          <span className="flex min-w-0 items-baseline gap-2">
            <span className="shrink-0 font-mono text-xs">{toolLabel(call.name)}</span>
            <span className="truncate font-mono text-xs text-muted-foreground">
              {toolSummary(call.input)}
            </span>
          </span>
        </summary>
        <div className="space-y-2 border-t border-border px-3 py-2">
          <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words bg-muted p-2 font-mono text-xs">
            {toolInput(call.input)}
          </pre>
          {call.result !== null && (
            <>
              <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words p-2 font-mono text-xs">
                {call.result.text}
              </pre>
              <Omitted count={call.result.omitted} />
            </>
          )}
        </div>
      </details>
    </li>
  )
}

export function ToolRows({ group }: { group: ToolGroup }): React.JSX.Element {
  return (
    <ul className="divide-y divide-border border border-border">
      {group.calls.map((call) => (
        <CallRow key={call.id} call={call} />
      ))}
    </ul>
  )
}

export interface ApprovalActions {
  /** Null when the viewer cannot decide: the session is over, or impersonation only reads. */
  decide: ((approvalId: string, approve: boolean) => void) | null
  pending: boolean
  sessionOver: boolean
}

const VERDICT = {
  open: { label: 'Needs approval', variant: 'warning' },
  approved: { label: 'Approved', variant: 'success' },
  declined: { label: 'Declined', variant: 'destructive' },
} as const

function verdict(request: ApprovalRequest): (typeof VERDICT)[keyof typeof VERDICT] {
  if (request.decision === null) return VERDICT.open
  return request.decision.approved ? VERDICT.approved : VERDICT.declined
}

function ApprovalOutcome({ request, sessionOver }: { request: ApprovalRequest; sessionOver: boolean }): React.JSX.Element {
  const decision = request.decision
  let outcome = 'The agent is waiting for a decision.'
  if (decision !== null) {
    outcome = `${decision.by} ${decision.approved ? 'approved' : 'declined'} the request.`
  }
  else if (sessionOver) outcome = 'Nobody decided before the session ended.'
  return <p className="text-sm text-muted-foreground">{outcome}</p>
}

function ApprovalButtons({
  request,
  decide,
  pending,
}: {
  request: ApprovalRequest
  decide: NonNullable<ApprovalActions['decide']>
  pending: boolean
}): React.JSX.Element {
  return (
    <div className="flex justify-end gap-2">
      <Button variant="outline" size="sm" disabled={pending} onClick={() => { decide(request.id, false) }}>
        Decline
      </Button>
      <Button size="sm" disabled={pending} onClick={() => { decide(request.id, true) }}>
        Approve
      </Button>
    </div>
  )
}

export function ApprovalCard({ request, actions }: { request: ApprovalRequest; actions: ApprovalActions }): React.JSX.Element {
  const decide = request.decision === null ? actions.decide : null
  const shown = verdict(request)
  return (
    <section
      aria-label={`Approval request for ${toolLabel(request.tool)}`}
      className={cn('space-y-3 border p-3', decide !== null ? 'border-warning/60' : 'border-border')}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-serif text-base">The agent asks to use {toolLabel(request.tool)}</h3>
        <Badge variant={shown.variant}>{shown.label}</Badge>
      </div>
      <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words bg-muted p-2 font-mono text-xs">
        {toolInput(request.input)}
      </pre>
      {decide !== null ? (
        <ApprovalButtons request={request} decide={decide} pending={actions.pending} />
      ) : (
        <ApprovalOutcome request={request} sessionOver={actions.sessionOver} />
      )}
    </section>
  )
}

export interface LaunchActions {
  /** Null when the viewer cannot decide: the session is over, or impersonation only reads. */
  decide: ((requestId: string, approve: boolean, reason: string | null) => void) | null
  pending: boolean
  sessionOver: boolean
}

const LAUNCH_BADGE: Record<
  LaunchState['kind'],
  { label: string; variant: 'warning' | 'secondary' | 'success' | 'destructive' }
> = {
  pending: { label: 'Needs approval', variant: 'warning' },
  approved: { label: 'Launching', variant: 'secondary' },
  launched: { label: 'Launched', variant: 'success' },
  declined: { label: 'Declined', variant: 'destructive' },
  failed: { label: 'Not launched', variant: 'destructive' },
}

interface Fact {
  label: string
  value: React.ReactNode
}

/**
 * The api's launch kinds, in words. An unknown kind shows as the api sent
 * it. A request trains a set; `p0` is the card of a one-task request from
 * before TRA-1477, which a past session's transcript still shows.
 */
const LAUNCH_KIND_LABEL: Record<string, string> = {
  'sft-set': 'SFT on published data',
  p0: 'RL on one task variant',
}

function launchKindLabel(kind: string): string {
  return LAUNCH_KIND_LABEL[kind] ?? kind
}

function launchFacts(request: LaunchRequest): Fact[] {
  const { preview, ask } = request
  const mono = (value: string): React.ReactNode => <span className="font-mono text-xs">{value}</span>
  const facts: Fact[] = [
    { label: 'Kind', value: launchKindLabel(preview.kind) },
    { label: 'Trains on', value: preview.trainsOn },
    { label: 'Preset', value: mono(preview.preset) },
  ]
  if (preview.changes.length > 0) {
    // What the agent changed in the derived preset (TRA-1345), beside the value it replaces.
    facts.push({
      label: 'Changes',
      value: (
        <ul className="space-y-0.5 font-mono text-xs">
          {preview.changes.map((change) => (
            <li key={change.field}>
              {`${presetFieldLabel(change.field)} ${presetValue(change.derived)} → ${presetValue(change.chosen)}`}
            </li>
          ))}
        </ul>
      ),
    })
  }
  facts.push(
    { label: 'Trainer', value: mono(preview.trainerRef) },
    { label: 'Compute', value: preview.compute },
    { label: 'Deploys to', value: preview.deploy },
  )
  if (preview.epochs !== null) facts.push({ label: 'Epochs', value: String(preview.epochs) })
  if (ask.expectedSteps !== null) {
    facts.push({ label: 'Expected steps', value: String(ask.expectedSteps) })
  }
  const digests = Object.entries(preview.digests)
  if (digests.length > 0) {
    facts.push({
      label: 'Hashes',
      value: (
        <ul className="space-y-0.5 font-mono text-xs">
          {digests.map(([file, digest]) => (
            <li key={file} title={digest}>
              {file} {digest.slice(0, 16)}
            </li>
          ))}
        </ul>
      ),
    })
  }
  return facts
}

function LaunchFacts({ request }: { request: LaunchRequest }): React.JSX.Element {
  return (
    <dl className="grid grid-cols-[7rem_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-sm">
      {launchFacts(request).map(({ label, value }) => (
        <div key={label} className="contents">
          <dt className={LABEL}>{label}</dt>
          <dd className="min-w-0 break-words">{value}</dd>
        </div>
      ))}
    </dl>
  )
}

function LaunchCaseText({ request }: { request: LaunchRequest }): React.JSX.Element {
  const { ask } = request
  return (
    <div className="space-y-2 text-sm">
      <p className="whitespace-pre-wrap break-words">{ask.summary}</p>
      {ask.checks.length > 0 && (
        <div className="space-y-1">
          <p className={LABEL}>Checks</p>
          <ul className="list-disc space-y-0.5 pl-5">
            {ask.checks.map((check) => (
              <li key={check}>{check}</li>
            ))}
          </ul>
        </div>
      )}
      {ask.risks.length > 0 && (
        <div className="space-y-1">
          <p className={LABEL}>Risks</p>
          <ul className="list-disc space-y-0.5 pl-5">
            {ask.risks.map((risk) => (
              <li key={risk}>{risk}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function unreported(by: string): string {
  return `${by} approved it, and the launch did not report back.`
}

function LaunchOutcome({
  state,
  lapsed,
  sessionOver,
}: {
  state: LaunchState
  lapsed: boolean
  sessionOver: boolean
}): React.JSX.Element {
  switch (state.kind) {
    case 'pending':
      return (
        <p className="text-sm text-muted-foreground">
          {sessionOver ? 'Nobody decided before the session ended.' : 'The agent is waiting for a decision.'}
        </p>
      )
    case 'approved':
      return (
        <p className="text-sm text-muted-foreground">
          {lapsed ? unreported(state.by) : `${state.by} approved it, and the run is starting.`}
        </p>
      )
    case 'launched':
      return (
        <p className="text-sm text-muted-foreground">
          {state.by} approved it, and it launched as run{' '}
          <span className="font-mono text-xs text-foreground">{state.runId}</span>.{' '}
          <Link to="/training" className="underline underline-offset-2">
            Open the Runs tab
          </Link>
        </p>
      )
    case 'declined':
      return (
        <p className="text-sm text-muted-foreground">
          {state.reason === ''
            ? `${state.by} declined it without a reason.`
            : `${state.by} declined it. Reason: ${state.reason}`}
        </p>
      )
    case 'failed':
      return (
        <p className="text-sm text-destructive">
          {state.by} approved it, and the launch was refused: {state.error}
        </p>
      )
  }
}

function LaunchDecision({
  request,
  decide,
  pending,
}: {
  request: LaunchRequest
  decide: NonNullable<LaunchActions['decide']>
  pending: boolean
}): React.JSX.Element {
  const [reason, setReason] = useState('')
  const id = useId()
  const typed = reason.trim()
  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <label htmlFor={id} className="sr-only">
        Reason for declining
      </label>
      <Input
        id={id}
        value={reason}
        maxLength={2000}
        placeholder="Reason for declining (optional)"
        className="h-9 min-w-0 flex-1"
        disabled={pending}
        onChange={(event) => {
          setReason(event.target.value)
        }}
      />
      <Button
        variant="outline"
        size="sm"
        disabled={pending}
        onClick={() => {
          decide(request.id, false, typed === '' ? null : typed)
        }}
      >
        Decline
      </Button>
      <Button
        size="sm"
        disabled={pending}
        onClick={() => {
          decide(request.id, true, null)
        }}
      >
        Approve and launch
      </Button>
    </div>
  )
}

/**
 * An approval whose launch never reported back (the api died mid-launch)
 * expires, and the card then offers the decision again (TRA-1347). The api
 * records the run that launch started, if it started one.
 */
export function LaunchCard({ request, actions }: { request: LaunchRequest; actions: LaunchActions }): React.JSX.Element {
  const { state } = request
  const lapsed = useHasPassed(state.kind === 'approved' ? state.expiresAt : null)
  const decide = state.kind === 'pending' || lapsed ? actions.decide : null
  const badge = LAUNCH_BADGE[lapsed ? 'pending' : state.kind]
  return (
    <section
      aria-label="Launch request"
      className={cn('space-y-3 border p-3', decide !== null ? 'border-warning/60' : 'border-border')}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-serif text-base">The agent asks to launch a training run</h3>
        <Badge variant={badge.variant}>{badge.label}</Badge>
      </div>
      <LaunchFacts request={request} />
      <LaunchCaseText request={request} />
      {decide !== null ? (
        <>
          {state.kind === 'approved' && (
            <p className="text-sm text-muted-foreground">
              {unreported(state.by)} If it started a run, deciding again records that run.
            </p>
          )}
          <LaunchDecision request={request} decide={decide} pending={actions.pending} />
        </>
      ) : (
        <LaunchOutcome state={state} lapsed={lapsed} sessionOver={actions.sessionOver} />
      )}
    </section>
  )
}

export function NoteRow({ note }: { note: Note }): React.JSX.Element {
  return (
    <p
      className={cn(
        'flex flex-wrap items-baseline gap-2 border-y py-2 text-sm',
        note.isError ? 'border-destructive/40 text-destructive' : 'border-border text-muted-foreground',
      )}
    >
      <span className="font-mono text-[10px] uppercase tracking-wider">{note.label}</span>
      {note.text !== '' && <span>{note.text}</span>}
    </p>
  )
}
